import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';
import { Button } from "@/components/ui/button";
import { ArrowRight, Printer, AlertCircle } from "lucide-react";
import { useLoader } from "../contexts/LoaderContext";
import { toast } from "sonner";
import IntellendLogo from "@/components/IntellendLogo";
import { useJourney } from "../contexts/JourneyContext";

interface KFSData {
    borrower_name: string;

    lender_details: {
        name: string;
        logo: string | null;
        privacy_policy: string;
        terms: string;
    };

    date_of_application: string;
    loan_proposal_no: string;
    loan_type: string;

    sanctioned_amount: number;
    disbursal_schedule: string;
    loan_term: string;

    instalment_type: string;
    number_of_epis: number;
    epi_amount: number;

    repayment_commencement: string;
    Interest_rate_to_show: string;
    additional_info_floating: string;

    /* Fees */
    processing_fees: number;
    insurance_charges: number;
    nach_fee: number;
    valuation_fees: string;
    other_fees: string;

    apr: string;

    /* Penal & Other Charges */
    penal_charges: string;
    other_penal_charges: string;
    foreclosure_charges: string;
    switching_charges: string;
    any_other_contingent_charges: string;

    /* 🔹 Extra backend fields (flat) */
    lender: string;
    lender_full_name: string;
    data_source: string;
    snap_date: string;
    expiry_date: string;
    offer_type: string;
    final_policy_reason: string;
    eligibility_score: number;

    approved_limit: number;
    offer_amount: number;
    offer_amount_winterest: number;

    daily_repayment_gross: number;
    interest_rate_monthly: number;
    interest_rate_monthly_to_show: string;

    proc_fee_pct: number;
    gst_on_proc_pct: number;
    insurance_fee_pct: number;

    proc_fee_amt: number;
    gst_on_proc_amt: number;
    insurance_fee_amt: number;

    deduction: number;
    deduction_text: string;
    net_disbursal: number;

    loan_start_date: string;
    loan_tenure: number;
    loan_tenure_to_show: string;

    repay_type: string;
    epi: number;
    repayment_start_date: string;

    proc_fee_amt_wgst: number;
    proc_fee_amt_wgst_re: number;
    proc_fee_amt_wgst_re_third_party: number;

    insurance_fee_amt_re: number;
    insurance_fee_amt_re_third_party: number;

    nach_fee_re: number;
    nach_fee_third_party: number;

    penal_charge: string;
    fc_charge: string;
    cool_off_period: string;

    clause_rec: string;
    clause_gr: string;
    gr_officer_phone: string;
    gr_officer_email: string;

    future_tran_to_other_re: string;
    details_lsp: string;

    total_interest_to_pay: number;
    total_fee_re: number;
    total_fee_third_party: number;

    repayment_schedule: Array<{
        EDI_Day: number;
        EDI_Date: string;
        EDI_Repayments: number;
    }>;
}

const FinalKFS = ({ isBuilderMode = false }: { isBuilderMode?: boolean }) => {
    const navigate = useNavigate();
    const { setLoading } = useLoader();
    const { refresh: refreshJourney } = useJourney();
    const [data, setData] = useState<KFSData | null>(null);

    useEffect(() => {
        if (isBuilderMode) return;
        const fetchKFS = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${API_BASE_URL}/api/get_final_kfs_details/`);
                setData(response.data);
            } catch (error) {
                console.error("Failed to fetch KFS details", error);
                toast.error("Failed to load Key Fact Statement details.");
            } finally {
                setLoading(false);
            }
        };

        fetchKFS();
    }, [isBuilderMode, setLoading]);

    const handleNext = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/process_loan_decision/`);

            if (response.status === 200) {
                await refreshJourney();
                // Backend already advanced the stage; reload /loan-application
                // which will render the correct next component (reject, waiting, or next step)
                window.location.href = "/loan-application";
            }
        } catch (error: any) {
            console.error("Decision error:", error);
            toast.error(error.response?.data?.error || "Failed to process loan decision.");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number | string) => {
        if (typeof amount === 'string') return amount;
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    if (!data) return null;

    if (isBuilderMode) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
                <div className="text-2xl mb-1">📋</div>
                <p className="font-semibold text-gray-500">Final KFS</p>
                <p className="text-xs mt-1">Displays Key Fact Statement document, requires user acceptance, and advances journey.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-12 font-sans">
            <style dangerouslySetInnerHTML={{ __html: `
                .mas-kfs-table-container::-webkit-scrollbar {
                    height: 8px;
                    display: block !important;
                }
                .mas-kfs-table-container::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                .mas-kfs-table-container::-webkit-scrollbar-thumb {
                    background: #003366;
                    border-radius: 4px;
                }
                .mas-kfs-table-container::-webkit-scrollbar-thumb:hover {
                    background: #002244;
                }
                /* For Firefox */
                .mas-kfs-table-container {
                    scrollbar-width: auto;
                    scrollbar-color: #003366 #f1f1f1;
                }
            ` }} />
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-10 no-print shadow-sm">
                <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <IntellendLogo size="sm" />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => window.print()}>
                            <Printer className="w-4 h-4 mr-2" /> Print
                        </Button>
                    </div>
                </div>
            </div>

            <main className="max-w-5xl mx-auto md:px-4 md:py-8 px-0 py-0">
                {/* Always show MAS design regardless of lender */}
                {true ? (
                    /* ---------------------------------------------------------
                       MAS DESIGN (Always shown)
                    --------------------------------------------------------- */
                    <div className="bg-white md:rounded-xl shadow-sm md:border overflow-hidden mb-8">
                        <div className="p-6 border-b bg-[#003366] text-white flex justify-between items-center">
                            <div>
                                <h1 className="text-2xl font-bold">KEY FACTS STATEMENT</h1>
                                <p className="text-gray-200 text-sm mt-1">Part 1 (Interest rate and fees)</p>
                            </div>
                            {data.lender_details.logo && (
                                <img src={data.lender_details.logo} alt={data.lender_details.name} className="h-10 object-contain" />
                            )}
                        </div>

                        <div className="p-6">
                            <div className="overflow-x-auto mas-kfs-table-container border border-gray-300 rounded-lg mb-6">
                                <table className="w-full border-collapse text-sm min-w-[800px]">
                                <tbody>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">1</td>
                                        <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Loan proposal/ account No.</td>
                                        <td className="p-3">{data.loan_proposal_no}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">2</td>
                                        <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Sanctioned Loan amount (in Rupees)</td>
                                        <td className="p-3">Rs. {data.sanctioned_amount.toFixed(1)}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">3</td>
                                        <td className="w-1/3 p-3 border-r bg-gray-50">
                                            <span className="font-bold">Disbursal schedule</span><br />
                                            <span className="text-xs text-gray-600">
                                                (i) Disbursement in stages or 100% upfront.<br />
                                                (ii) If it is stage wise, mention the clause of loan agreement having relevant details
                                            </span>
                                        </td>
                                        <td className="p-3">{data.disbursal_schedule}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">4</td>
                                        <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Loan term (year/months/days)</td>
                                        <td className="p-3">{data.loan_term}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">5</td>
                                        <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Instalment details</td>
                                        <td className="p-0">
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-100 border-b">
                                                        <th className="p-2 border-r text-xs font-bold text-center">Type of instalments</th>
                                                        <th className="p-2 border-r text-xs font-bold text-center">Number of EPIs</th>
                                                        <th className="p-2 border-r text-xs font-bold text-center">EPI (₹)</th>
                                                        <th className="p-2 text-xs font-bold text-center">Commencement of repayment</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="p-2 border-r text-center">{data.instalment_type}</td>
                                                        <td className="p-2 border-r text-center">{data.number_of_epis}</td>
                                                        <td className="p-2 border-r text-center">Rs. {data.epi_amount.toFixed(2)}</td>
                                                        <td className="p-2 text-center">{data.repayment_commencement}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">6</td>
                                        <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Interest rate (%) and type (fixed or floating or hybrid)</td>
                                        <td className="p-3">{data.Interest_rate_to_show}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">7</td>
                                        <td className="p-3 font-bold bg-gray-50" colSpan={2}>Additional Information in case of Floating rate of interest</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="p-0" colSpan={3}>
                                            <div className="overflow-x-auto mas-kfs-table-container">
                                                <table className="w-full border-collapse">
                                                    <thead>
                                                        <tr className="bg-gray-100 border-b">
                                                            <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Reference Benchmark</th>
                                                            <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Benchmark rate (%) (B)</th>
                                                            <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Spread (%) (S)</th>
                                                            <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Final rate (%) R = (B) + (S)</th>
                                                            <th className="p-2 border-r text-xs font-bold text-center" rowSpan={2}>Reset periodicity (Months)</th>
                                                            <th className="p-2 border-b text-xs font-bold text-center" colSpan={2}>Impact of change in the reference benchmark</th>
                                                        </tr>
                                                        <tr className="bg-gray-100 border-b">
                                                            <th className="p-2 border-r text-xs font-bold text-center">EPI (₹)</th>
                                                            <th className="p-2 text-xs font-bold text-center">No. of EPIs</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td className="p-2 border-r text-center"></td>
                                                            <td className="p-2 border-r text-center"></td>
                                                            <td className="p-2 border-r text-center"></td>
                                                            <td className="p-2 border-r text-center"></td>
                                                            <td className="p-2 border-r text-center"></td>
                                                            <td className="p-2 border-r text-center"></td>
                                                            <td className="p-2 text-center"></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">8</td>
                                        <td className="p-3 font-bold bg-gray-50" colSpan={2}>Fee/ Charges</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="p-0" colSpan={3}>
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-100 border-b text-center font-bold text-[10px]">
                                                        <th className="p-2 border-r" colSpan={2}></th>
                                                        <th className="p-2 border-r" colSpan={2}>8A Payable to RE</th>
                                                        <th className="p-2" colSpan={2}>8B Payable to Third Party</th>
                                                    </tr>
                                                    <tr className="bg-gray-100 border-b">
                                                        <th className="p-2 border-r" colSpan={2}></th>
                                                        <th className="p-2 border-r text-xs font-bold text-center">One-time/ Recurring</th>
                                                        <th className="p-2 border-r text-xs font-bold text-center">Amount (in ₹) or Percentage (%)</th>
                                                        <th className="p-2 border-r text-xs font-bold text-center">One-time/ Recurring</th>
                                                        <th className="p-2 text-xs font-bold text-center">Amount (in ₹) or Percentage (%)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="border-b">
                                                        <td className="w-12 p-2 border-r text-center">(i)</td>
                                                        <td className="w-1/4 p-2 border-r font-bold">Processing fees</td>
                                                        <td className="p-2 border-r text-center">One-Time</td>
                                                        <td className="p-2 border-r text-center">Rs. {data.proc_fee_amt_wgst_re}</td>
                                                        <td className="p-2 border-r text-center">One-Time</td>
                                                        <td className="p-2 text-center">Rs. {data.proc_fee_amt_wgst_re_third_party}</td>
                                                    </tr>
                                                    <tr className="border-b">
                                                        <td className="w-12 p-2 border-r text-center">(ii)</td>
                                                        <td className="w-1/4 p-2 border-r font-bold">Insurance charges</td>
                                                        <td className="p-2 border-r text-center">One-Time</td>
                                                        <td className="p-2 border-r text-center">Rs. {data.insurance_fee_amt_re}</td>
                                                        <td className="p-2 border-r text-center">One-Time</td>
                                                        <td className="p-2 text-center">Rs. {data.insurance_fee_amt_re_third_party}</td>
                                                    </tr>
                                                    <tr className="border-b">
                                                        <td className="w-12 p-2 border-r text-center">(iii)</td>
                                                        <td className="w-1/4 p-2 border-r font-bold">Valuation fees</td>
                                                        <td className="p-2 border-r text-center"></td>
                                                        <td className="p-2 border-r text-center"></td>
                                                        <td className="p-2 border-r text-center"></td>
                                                        <td className="p-2 text-center"></td>
                                                    </tr>
                                                    <tr className="border-b">
                                                        <td className="w-12 p-2 border-r text-center">(iv)</td>
                                                        <td className="w-1/4 p-2 border-r font-bold">Any other (please specify)</td>
                                                        <td className="p-2 border-r text-center"></td>
                                                        <td className="p-2 border-r text-center"></td>
                                                        <td className="p-2 border-r text-center"></td>
                                                        <td className="p-2 text-center"></td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">9</td>
                                        <td className="w-1/3 p-3 border-r font-bold bg-gray-50">Annual Percentage Rate (APR) (%)</td>
                                        <td className="p-3">{data.apr}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">10</td>
                                        <td className="p-3 font-bold bg-gray-50" colSpan={2}>Details of Contingent Charges (in ₹ or %, as applicable)</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(i)</td>
                                        <td className="w-1/3 p-3 border-r font-bold">Penal charges, if any, in case of delayed payment</td>
                                        <td className="p-3">{data.penal_charge}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(ii)</td>
                                        <td className="w-1/3 p-3 border-r font-bold">Other penal charges, if any</td>
                                        <td className="p-3"></td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(iii)</td>
                                        <td className="w-1/3 p-3 border-r font-bold">Foreclosure charges, if applicable</td>
                                        <td className="p-3">{data.fc_charge}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(iv)</td>
                                        <td className="w-1/3 p-3 border-r font-bold">Charges for switching of loans from floating to fixed rate and vice versa</td>
                                        <td className="p-3"></td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">(v)</td>
                                        <td className="w-1/3 p-3 border-r font-bold">Any other charges (please specify)</td>
                                        <td className="p-0">
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="border-b text-xs font-bold">
                                                        <th className="p-2 text-left">Particulars</th>
                                                        <th className="p-2 text-right">Charges Upto</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="text-xs">
                                                        <td className="p-2">Payment Instrument Swapping charges</td>
                                                        <td className="p-2 text-right">Rs.250/-</td>
                                                    </tr>
                                                    <tr className="text-xs">
                                                        <td className="p-2">No Overdue Certificate on special request</td>
                                                        <td className="p-2 text-right">Rs.500/-</td>
                                                    </tr>
                                                    <tr className="text-xs">
                                                        <td className="p-2">Duplicate NOC</td>
                                                        <td className="p-2 text-right">Rs.500/-</td>
                                                    </tr>
                                                    <tr className="text-xs">
                                                        <td className="p-2">List of Documents (2nd time during F.Y.)</td>
                                                        <td className="p-2 text-right">Rs.250/-</td>
                                                    </tr>
                                                    <tr className="text-xs">
                                                        <td className="p-2">Copy of Sanction Letter (2nd time)</td>
                                                        <td className="p-2 text-right">Rs.250/-</td>
                                                    </tr>
                                                    <tr className="text-xs">
                                                        <td className="p-2">Copy of Welcome Letter (2nd time)</td>
                                                        <td className="p-2 text-right">Rs.250/-</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className="bg-[#003366] text-white p-2 font-bold text-center mb-4">
                                Part 2 (Other qualitative information)
                            </div>

                            <table className="w-full border-collapse border border-gray-300 text-sm mb-6">
                                <tbody>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">1</td>
                                        <td className="w-1/3 p-3 border-r bg-gray-50">Clause of Loan agreement relating to engagement of recovery agents</td>
                                        <td className="p-3">---</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">2</td>
                                        <td className="w-1/3 p-3 border-r bg-gray-50">Clause of Loan agreement which details grievance redressal mechanism</td>
                                        <td className="p-3">---</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">3</td>
                                        <td className="w-1/3 p-3 border-r bg-gray-50">Phone number and email id of the nodal grievance redressal officer</td>
                                        <td className="p-3">
                                            <div className="text-xs space-y-1">
                                                <p><span className="font-bold">Name:</span> Ms. Preksha Bharucha</p>
                                                <p><span className="font-bold">Designation:</span> Grievance Redressal Officer</p>
                                                <p><span className="font-bold">E-Mail Id:</span> <a href="mailto:mfsl@mas.co.in" className="text-blue-600">mfsl@mas.co.in</a></p>
                                                <p><span className="font-bold">Contact No:</span> 079-41106751</p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">4</td>
                                        <td className="w-1/3 p-3 border-r bg-gray-50">Whether the loan is, or in future maybe, subject to transfer to other REs or securitisation (Yes / No)</td>
                                        <td className="p-3">{data.future_tran_to_other_re}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">5</td>
                                        <td className="bg-gray-50 p-3" colSpan={2}>
                                            <div className="text-xs mb-2">In case of lending under collaborative lending arrangements (e.g., co-lending/ outsourcing), following additional details may be furnished:</div>
                                            <table className="w-full border-collapse border border-gray-200 mt-2">
                                                <thead>
                                                    <tr className="bg-gray-100 border-b">
                                                        <th className="p-2 border-r text-center">Name of the originating RE, along with its funding proportion</th>
                                                        <th className="p-2 border-r text-center">Name of the partner RE along with its proportion of funding</th>
                                                        <th className="p-2 text-center">Blended rate of interest</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="p-2 border-r text-center">NA</td>
                                                        <td className="p-2 border-r text-center">NA</td>
                                                        <td className="p-2 text-center">NA</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="w-12 p-3 border-r font-bold text-center bg-gray-50">6</td>
                                        <td className="bg-gray-50 p-3" colSpan={2}>
                                            <div className="text-xs mb-2">In case of digital loans, following specific disclosures may be furnished:</div>
                                            <table className="w-full border-collapse border border-gray-200 mt-2">
                                                <tbody>
                                                    <tr className="border-b">
                                                        <td className="p-2 border-r">(i) Cooling off/look-up period, in terms of RE's board approved policy, during which borrower shall not be charged any penalty on prepayment of loan</td>
                                                        <td className="p-2 text-center">{data.cool_off_period}</td>
                                                    </tr>
                                                    <tr className="border-b">
                                                        <td className="p-2 border-r">(ii) Details of LSP acting as recovery agent and authorized to approach the borrower</td>
                                                        <td className="p-2 text-center">
                                                            <a href="https://mas.co.in/wp-content/uploads/Recovery-Agent-31-12-2025.pdf" className="text-blue-600 break-all text-[10px]" target="_blank" rel="noopener noreferrer">
                                                                https://mas.co.in/wp-content/uploads/Recovery-Agent-31-12-2025.pdf
                                                            </a>
                                                            <br />& Analog Legalhub Technology Solutions Pvt. Ltd.
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>

                            {/* MAS Annexure B */}
                            <div className="mt-8">
                                <h2 className="bg-[#003366] text-white font-bold p-2 text-center mb-0 uppercase tracking-wide text-sm">
                                    Annexure B - Computation of APR for Retail and MSME loans
                                </h2>
                                <div className="border border-gray-300 overflow-x-auto mas-kfs-table-container">
                                    <table className="w-full border-collapse text-xs">
                                        <thead>
                                            <tr className="bg-gray-100 border-b text-center font-bold">
                                                <th className="w-10 p-2 border-r">S. I.</th>
                                                <th className="p-2 border-r text-left min-w-[180px]">Parameter</th>
                                                <th className="p-2 text-left min-w-[140px]">Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">1</td>
                                                <td className="p-2 border-r font-medium">Sanctioned Loan amount (in Rupees) (Sl no. 2 of the KFS– Part 1)</td>
                                                <td className="p-2">Rs. {data.sanctioned_amount.toFixed(2)}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">2</td>
                                                <td className="p-2 border-r font-medium">Loan Term (in years/ months/ days) (Sl No.4 of the KFS– Part 1)</td>
                                                <td className="p-2">{data.loan_term}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">a)</td>
                                                <td className="p-2 border-r">No. of instalments for payment of principal, in case of non- equated periodic loans</td>
                                                <td className="p-2">{data.number_of_epis}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">b)</td>
                                                <td className="p-2 border-r">
                                                    <span className="font-bold">Type of EPI</span><br />
                                                    Amount of each EPI (in Rupees) and nos. of EPIs (e.g., no. of EMIs in case of monthly instalments) (Sl No. 5 of the KFS– Part 1)
                                                </td>
                                                <td className="p-2">
                                                    Type of EPI – {data.repay_type}<br />
                                                    EPI – Rs. {data.epi_amount.toFixed(2)}<br />
                                                    Number of EPIs - {data.number_of_epis}
                                                </td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">c)</td>
                                                <td className="p-2 border-r">No. of instalments for payment of capitalised interest, if any</td>
                                                <td className="p-2">NA</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">d)</td>
                                                <td className="p-2 border-r">Commencement of repayments, post sanction (Sl No. 5 of the KFS– Part 1)</td>
                                                <td className="p-2">{data.repayment_commencement}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">3</td>
                                                <td className="p-2 border-r font-medium">Interest rate type (fixed or floating or hybrid) (Sl No. 6 of the KFS– Part 1)</td>
                                                <td className="p-2">Fixed</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">4</td>
                                                <td className="p-2 border-r font-medium">Rate of Interest (Sl No. 6 of the KFS– Part 1)</td>
                                                <td className="p-2">{data.Interest_rate_to_show}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">5</td>
                                                <td className="p-2 border-r font-medium">Total Interest Amount to be charged during the entire tenor of the loan as per the rate prevailing on the date of loan agreement (Sl No. a of the KFS–Part 1)</td>
                                                <td className="p-2">Rs. {data.total_interest_to_pay.toFixed(2)}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">6</td>
                                                <td className="p-2 border-r font-medium">Fee/ Charges payable (in Rupees)</td>
                                                <td className="p-2 font-bold">Rs. {(data.total_fee_re + data.total_fee_third_party).toFixed(2)}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">A</td>
                                                <td className="p-2 border-r font-medium">Payable to the RE (Sl No.8A of the KFS -Part 1)</td>
                                                <td className="p-2">Rs. {data.total_fee_re.toFixed(2)}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">B</td>
                                                <td className="p-2 border-r font-medium">Payable to third-party routed through RE (Sl No.8B of the KFS- Part 1)</td>
                                                <td className="p-2">Rs. {data.total_fee_third_party.toFixed(2)}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">7</td>
                                                <td className="p-2 border-r font-medium">Net disbursed amount (1-6) (in Rupees)</td>
                                                <td className="p-2">Rs. {(data.sanctioned_amount - (data.total_fee_re + data.total_fee_third_party)).toFixed(2)}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">8</td>
                                                <td className="p-2 border-r font-medium">Total amount to be paid by the borrower (sum of 1 and 5) (in Rupees)</td>
                                                <td className="p-2">Rs. {(data.sanctioned_amount + data.total_interest_to_pay).toFixed(2)}</td>
                                            </tr>
                                            <tr className="border-b">
                                                <td className="p-2 border-r text-center">9</td>
                                                <td className="p-2 border-r font-medium">Annual Percentage rate- Effective annualized interest rate (in percentage) (Sl No.9 of the KFS -Part 1)</td>
                                                <td className="p-2">{data.apr}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* MAS REPAYMENT SCHEDULE */}
                            <div className="mt-8">
                                <h2 className="bg-[#003366] text-white font-bold p-2 text-center mb-0 uppercase tracking-wide text-sm">
                                    Annexure C - Repayment Schedule
                                </h2>
                                <div className="border border-gray-300 overflow-x-auto mas-kfs-table-container">
                                    <table className="w-full text-xs border-collapse">
                                        <thead className="bg-gray-100 border-b">
                                            <tr>
                                                <th className="p-2 border-r font-bold text-center">Instalment No.</th>
                                                <th className="p-2 border-r font-bold text-center min-w-[100px]">Outstanding Principal (₹)</th>
                                                <th className="p-2 border-r font-bold text-center min-w-[80px]">Principal (₹)</th>
                                                <th className="p-2 border-r font-bold text-center min-w-[80px]">Interest (₹)</th>
                                                <th className="p-2 font-bold text-center min-w-[80px]">Instalment (₹)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.repayment_schedule.map((row: any) => (
                                                <tr key={row.EDI_Day} className="border-b hover:bg-gray-50 transition-colors">
                                                    <td className="p-2 border-r text-center">{row.EDI_Day}</td>
                                                    <td className="p-2 border-r text-right">{(row.POS_After_Payment || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                    <td className="p-2 border-r text-right">{(row.Principal_Component || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                    <td className="p-2 border-r text-right">{(row.Interest_Component || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                    <td className="p-2 text-right font-medium">{(row.EDI_Repayments || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* ---------------------------------------------------------
                       DEFAULT DESIGN
                    --------------------------------------------------------- */
                    <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-8">
                        <div className="p-6 border-b bg-gray-50/50 flex justify-between items-center">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Key Fact Statement (Final)</h1>
                                <p className="text-gray-500 text-sm mt-1">
                                    Please review the final loan terms from {data.lender_details.name} before proceeding.
                                </p>
                            </div>
                            {data.lender_details.logo && (
                                <img src={data.lender_details.logo} alt={data.lender_details.name} className="h-10 object-contain" />
                            )}
                        </div>

                        <div className="p-6">
                            {/* Section: Loan Details */}
                            <div className="mb-8">
                                <h2 className="bg-gray-100 text-gray-800 font-semibold px-4 py-2 rounded-t-lg border border-b-0 text-sm uppercase tracking-wide">
                                    Loan Details
                                </h2>
                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <tbody>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-medium text-gray-700">Loan proposal/ account No.</td>
                                            <td className="p-3 text-gray-900">{data.loan_proposal_no}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-medium text-gray-700">Type of Loan</td>
                                            <td className="p-3 text-gray-900">{data.loan_type}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 bg-gray-50 font-medium text-gray-700">Sanctioned Loan amount (in Rupees)</td>
                                            <td className="p-3 text-gray-900 font-semibold">{formatCurrency(data.sanctioned_amount)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 bg-gray-50 font-medium text-gray-700">Disbursal schedule</td>
                                            <td className="p-3 text-gray-900">{data.disbursal_schedule}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 bg-gray-50 font-medium text-gray-700">Loan term (year/months/days)</td>
                                            <td className="p-3 text-gray-900">{data.loan_term}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Section: Instalment details */}
                            <div className="mb-8">
                                <h2 className="bg-gray-100 text-gray-800 font-semibold px-4 py-2 border border-b-0 text-sm uppercase tracking-wide">
                                    Instalment details
                                </h2>
                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <tbody>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-medium text-gray-700">Type of instalments</td>
                                            <td className="p-3 text-gray-900">{data.instalment_type}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 bg-gray-50 font-medium text-gray-700">Number of EPIs</td>
                                            <td className="p-3 text-gray-900">{data.number_of_epis}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 bg-gray-50 font-medium text-gray-700">EPI (₹)</td>
                                            <td className="p-3 text-gray-900 font-semibold text-intellend-green">{formatCurrency(data.epi_amount)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 bg-gray-50 font-medium text-gray-700">Commencement of repayment, post sanction</td>
                                            <td className="p-3 text-gray-900">{data.repayment_commencement}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="p-3 bg-gray-50 font-medium text-gray-700">Rate of Interest</td>
                                            <td className="p-3 text-gray-900">{data.Interest_rate_to_show}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Section: Fee/ Charges */}
                            <div className="mb-8">
                                <h2 className="bg-gray-100 text-gray-800 font-semibold px-4 py-2 border border-b-0 text-sm uppercase tracking-wide">
                                    Fee/ Charges
                                </h2>
                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <tbody>
                                        <tr className="border-b bg-gray-50">
                                            <td className="p-3 font-medium text-gray-700" colSpan={2}>
                                                Processing fees
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">Payable to RE</td>
                                            <td className="p-3 text-gray-900">{formatCurrency(data.proc_fee_amt_wgst_re)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">One-Time/ Recurring</td>
                                            <td className="p-3 text-gray-900">One-Time</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">Payable to third party through RE</td>
                                            <td className="p-3 text-gray-900">{formatCurrency(data.proc_fee_amt_wgst_re_third_party)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">One-Time/ Recurring</td>
                                            <td className="p-3 text-gray-900">One-Time</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <tbody>
                                        <tr className="border-b bg-gray-50">
                                            <td className="p-3 font-medium text-gray-700" colSpan={2}>
                                                Insurance charges
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">Payable to RE</td>
                                            <td className="p-3 text-gray-900">{formatCurrency(data.insurance_fee_amt_re)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">One-Time/ Recurring</td>
                                            <td className="p-3 text-gray-900">One-Time</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">Payable to third party through RE</td>
                                            <td className="p-3 text-gray-900">{formatCurrency(data.insurance_fee_amt_re_third_party)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">One-Time/ Recurring</td>
                                            <td className="p-3 text-gray-900">One-Time</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <tbody>
                                        <tr className="border-b bg-gray-50">
                                            <td className="p-3 font-medium text-gray-700" colSpan={2}>
                                                NACH Fee
                                            </td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">Payable to RE</td>
                                            <td className="p-3 text-gray-900">{formatCurrency(data.nach_fee_re)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">One-Time/ Recurring</td>
                                            <td className="p-3 text-gray-900">One-Time</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">Payable to third party through RE</td>
                                            <td className="p-3 text-gray-900">{formatCurrency(data.nach_fee_third_party)}</td>
                                        </tr>
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">One-Time/ Recurring</td>
                                            <td className="p-3 text-gray-900">One-Time</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="mb-8">
                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <tbody>

                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-bold text-gray-700">
                                                Annual Percentage Rate
                                            </td>
                                            <td className="p-3 text-gray-900 font-bold">
                                                {data.apr}
                                            </td>
                                        </tr>

                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-bold text-gray-700">
                                                Penal charges, if any, in case of delayed payment
                                            </td>
                                            <td className="p-3 text-gray-900 font-bold">
                                                {data.penal_charge}
                                            </td>
                                        </tr>

                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-bold text-gray-700">
                                                Foreclosure charges, if applicable
                                            </td>
                                            <td className="p-3 text-gray-900 font-bold">
                                                {data.fc_charge}
                                            </td>
                                        </tr>

                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-bold text-gray-700">
                                                Clause of Loan agreement relating to engagement of recovery agents
                                            </td>
                                            <td className="p-3 text-gray-900 font-bold">
                                                {data.clause_rec}
                                            </td>
                                        </tr>

                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-bold text-gray-700">
                                                Clause of Loan agreement which details grievance redressal mechanism
                                            </td>
                                            <td className="p-3 text-gray-900 font-bold">
                                                {data.clause_gr}
                                            </td>
                                        </tr>

                                    </tbody>
                                </table>

                            </div>

                            <div className="mb-8">

                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <tbody>

                                        {/* Processing Fees Header */}
                                        <tr className="border-b bg-gray-50">
                                            <td
                                                className="p-3 font-medium text-gray-700"
                                                colSpan={2}
                                            >
                                                Grievance redressal officer
                                            </td>

                                        </tr>

                                        {/* Payable to RE */}
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">
                                                Phone number
                                            </td>
                                            <td className="p-3 text-gray-900">
                                                {formatCurrency(data.gr_officer_phone)}
                                            </td>
                                        </tr>

                                        {/* Under rows */}
                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 text-gray-600 whitespace-nowrap">
                                                Email Id
                                            </td>
                                            <td className="p-3 text-gray-900">

                                                {formatCurrency(data.gr_officer_email)}
                                            </td>
                                        </tr>




                                    </tbody>
                                </table>
                            </div>
                            <div className="mb-8">
                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <tbody>

                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-bold text-gray-700">
                                                Whether the loan is, or in future maybe, subject to transfer to other REs or securitisation (Yes / No)
                                            </td>
                                            <td className="p-3 text-gray-900 font-bold">
                                                {data.future_tran_to_other_re}
                                            </td>
                                        </tr>

                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-bold text-gray-700">
                                                Cooling off / look-up period, in terms of RE’s board approved policy, during which borrower shall not be charged any penalty on prepayment of loan
                                            </td>
                                            <td className="p-3 text-gray-900 font-bold">
                                                {data.cool_off_period}
                                            </td>
                                        </tr>

                                        <tr className="border-b">
                                            <td className="w-1/3 p-3 bg-gray-50 font-bold text-gray-700">
                                                Details of LSP acting as recovery agent and authorized to approach the borrower
                                            </td>
                                            <td className="p-3 text-gray-900 font-bold">
                                                {data.details_lsp}<br />& Analog Legalhub Technology Solutions Pvt. Ltd.
                                            </td>
                                        </tr>

                                    </tbody>
                                </table>

                            </div>
                            {/* Repayment Schedule */}
                            {data.repayment_schedule && data.repayment_schedule.length > 0 && (
                                <div className="mb-8 overflow-hidden">
                                    <h2 className="bg-gray-100 text-gray-800 font-semibold px-4 py-2 rounded-t-lg border border-b-0 text-sm uppercase tracking-wide">
                                        Repayment Schedule
                                    </h2>
                                    <div className="max-h-[500px] overflow-y-auto border border-gray-200 rounded-b-lg mx-auto" style={{ maxWidth: '400px' }}>
                                        <table className="w-full text-sm border-collapse">
                                            <thead className="sticky top-0 bg-gray-50 shadow-sm">
                                                <tr className="border-b">
                                                    <th className="p-3 text-center font-medium text-gray-700">Day</th>
                                                    <th className="p-3 text-right font-medium text-gray-700 pr-6">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.repayment_schedule.map((row) => (
                                                    <tr key={row.EDI_Day} className="border-b hover:bg-gray-50 transition-colors">
                                                        <td className="p-3 text-gray-900 text-center">{row.EDI_Day}</td>
                                                        <td className="p-3 text-gray-900 text-right font-medium pr-6">{formatCurrency(row.EDI_Repayments)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg flex gap-3 text-sm text-blue-800 mb-8">
                    <AlertCircle className="h-5 w-5 shrink-0" />
                    <p>
                        By clicking "Confirm & Continue", you acknowledge that you have read and accepted the final loan terms and Key Fact Statement.
                    </p>
                </div>

                <div className="flex justify-center">
                    <Button
                        onClick={handleNext}
                        className="bg-intellend-green hover:bg-intellend-green/90 h-14 px-8 text-lg font-bold shadow-lg transition-all active:scale-[0.98]"
                    >
                        Confirm & Continue <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </main>
        </div>
    );
};

export default FinalKFS;
